import webuiState from "../app/InternalStateService";
import { createFormData } from "../utils/dataUtils";
import { getDependentPaths } from "../utils/storeUtils";
import { addQueryParams } from "../utils/urlUtils";
import { resolveBinding } from "../utils/bindingUtils";
import { recordUpdate, isObsoleteUpdate, finishUpdate } from "../utils/updateActionUtils";
import { WEBUILogs } from "@tinqin/tinqin-utils/src/logging";

//Summary:
//General purpose action for making a single request/response cycle for updating client data
//-can perform client side validation, trigger loading animations, send/update arbitrary client data
//-can be executed inside Widget and inside a Step without change of contract (FE implementation)
//Behaviour:
//validation -> show Loading -> generate request data -> send data ->
//      -> receive data -> save/ignore response on targetDependencies -> hide Loading
export function update(sirenAction, currentWidgetId, currentPath) {
    const clearUiStateValidationsDeep = webuiState.getActionByName(
        "component.clearUiStateValidationsDeep"
    );
    let updateActionDefaultConfig =
        webuiState.getActionsDefaultConfig({ widgetId: currentWidgetId })["update"] || {};

    return async (dispatch, getState) => {
        let {
            id: actionId,
            //POST/GET
            //default: POST
            method = sirenAction["method"] || "POST",
            //default: currentWidget url
            href = "",
            //default headers
            headers = {
                "Content-Type": "multipart/form-data"
            },
            //default: false
            //will trigger validation only on requestDependencies
            validation = updateActionDefaultConfig.validation || false,
            //After update data delete all validations in uiState.
            resetValidation = updateActionDefaultConfig.resetValidation || false,
            //dependencies for creating request data
            //default: [currentWidget]
            requestDependencies = [],
            //target dependencies for saving response data and triggering loading animation
            //default: [currentWidget]
            targetDependencies = [],
            //used to prevent response data to be saved on client side (for POST only requests)
            //default: false
            ignoreResponse = false,
            //used to wrap request in {"class":"request, entities: [<payload>]} even when target dependencies is single
            requestWrapper = updateActionDefaultConfig.requestWrapper,
            //"local", "widget", "global", "none"
            //default: "local" will trigger loading for targetDependencies
            loadingType = updateActionDefaultConfig.loadingType || "local",
            awaitActionResponse = true
        } = sirenAction;
        let actionResult;
        let requestPayload;
        let response;
        let currentWidgetEntity = getState().getIn(["data", currentWidgetId, "class", 0]);
        let defaultDependency = currentWidgetEntity;
        //in case of Flow widget and step entity
        //dependencies paths which start with step entity name,
        //will be updated to include parent flow widget entity name
        if (isFlowWidget(currentWidgetId, getState())) {
            let currentStepEntity = currentPath.split(".")[1];
            requestDependencies = requestDependencies.map(dependency => {
                if (dependency.startsWith(currentStepEntity)) {
                    return currentWidgetEntity + "." + dependency;
                }
                return dependency;
            });
            targetDependencies = targetDependencies.map(dependency => {
                if (dependency.startsWith(currentStepEntity)) {
                    return currentWidgetEntity + "." + dependency;
                }
                return dependency;
            });
            defaultDependency = currentWidgetEntity + "." + currentStepEntity;
        }
        //default request data
        if (!requestDependencies.length) {
            requestDependencies = [defaultDependency];
        }
        //default target
        if (!targetDependencies.length) {
            targetDependencies = [defaultDependency];
        }
        //default endpoint
        if (!href) {
            href = webuiState.getActionByName("widget.getWidgetUrl")(currentWidgetId, getState());
        }
        //trigger validation
        if (validation) {
            let validationResult = dispatch(
                validateDependencies(requestDependencies, currentPath, getState())
            );
            if (!validationResult) {
                return Promise.reject("update action: request dependencies validation failed.");
            }
        }
        //trigger loading animations
        dispatch(triggerLoadingAnimations(loadingType, currentPath, targetDependencies, true));
        //construct request payload
        if (method === "GET") {
            requestPayload = {};
            if (sirenAction.fields) {
                href = addQueryParams(href, sirenAction.fields);
            }
        } else {
            requestPayload = constructRequestEntity(
                requestDependencies,
                getState(),
                currentPath,
                sirenAction,
                currentWidgetId,
                requestWrapper
            );
        }
        //for synchronisation purpose between update actions with the same id
        let actionIndex;
        if (actionId) {
            actionIndex = dispatch(recordUpdate(actionId));
        }
        //send server request
        try {
            response = await dispatch(
                webuiState.getActionByName("request.request")(
                    {
                        method: method,
                        endpoint: href,
                        headers: headers,
                        payload: requestPayload
                    },
                    {
                        widgetId: currentWidgetId
                    }
                )
            );
            let isObsolete = false;
            //for synchronisation purpose between update actions with the same id
            if (actionId) {
                isObsolete = dispatch(isObsoleteUpdate({ actionId, actionIndex }));
            }
            //save response from server
            //unless "ignoreResponse" flag
            //or the action with the same id but executed later is ongoing
            if (ignoreResponse || isObsolete) {
                actionResult = response;
            } else {
                if (isCollectiveResponse(response) && isActionResponse(response)) {
                    const dispatchSirenActions = webuiState.getActionByName(
                        "siren.dispatchSirenActions"
                    );
                    actionResult = dispatch(
                        dispatchSirenActions(response.actions, currentWidgetId, currentPath)
                    );
                    if (awaitActionResponse) {
                        await actionResult;
                    }
                } else {
                    actionResult = await dispatch(
                        saveResponse(response, targetDependencies, getState(), currentPath)
                    );
                }
            }
        } catch (error) {
            WEBUILogs.error(error);
            actionResult = Promise.reject(error);
        } finally {
            //for synchronisation purpose between update actions with the same id
            if (actionId) {
                dispatch(finishUpdate({ actionId, actionIndex }));
            }
            //close loading animations after requests succeeds/fails
            dispatch(triggerLoadingAnimations(loadingType, currentPath, targetDependencies, false));
            targetDependencies.forEach(targetDependency => {
                let entityPath = resolveBinding(targetDependency, currentPath);
                if (resetValidation) {
                    dispatch(clearUiStateValidationsDeep(currentWidgetId, entityPath));
                } else
                    dispatch(
                        webuiState.getActionByName(
                            "validations.revalidateEntities"
                        )(currentWidgetId, { entityPath })
                    );
            });
        }
        return actionResult;
    };
}

function validateDependencies(dependencies = [], currentPath, state) {
    return dispatch => {
        let result = true;
        let storePaths = getDependentPaths(dependencies, state, currentPath);
        storePaths.forEach(storePath => {
            let entityDataImmutable = state.getIn(["data", ...storePath]);
            let entityData = entityDataImmutable ? entityDataImmutable.toJS() : {};
            //TODO this will not work with the new validation request format
            let validDependency = dispatch(
                webuiState.getActionByName("validations.validateEntity")(entityData, storePath)
            );
            result = result && validDependency;
        });
        return result;
    };
}

function triggerLoadingAnimations(loadingType, currentPath, targets, triggerFlag) {
    const triggerLoading = webuiState.getActionByName("loading.triggerLoading");
    const triggerGlobalLoading = webuiState.getActionByName("loading.triggerGlobalLoading");
    return (dispatch, getState) => {
        if (loadingType === "widget") {
            let targetEntities = getDependentPaths(targets, getState(), currentPath);
            targetEntities.forEach(entity => {
                dispatch(
                    webuiState.getActionByName("loading.triggerWidgetLoading")(
                        entity[0],
                        triggerFlag
                    )
                );
            });
        } else if (loadingType === "local") {
            return dispatch(triggerLoading(targets, currentPath, triggerFlag));
        } else if (loadingType === "global") {
            return dispatch(triggerGlobalLoading(triggerFlag));
        }
    };
}

//returns a single entity in case there a single requestDependency and requestWrapper is not set to true
//otherwise returns a request entity in the following format:
// {
// 	class: ["request"], (special request entity)
// 	entities: [
// 		{
// 			class: ["flow.cardDescription"], (special dependency holder entity)
// 			entities:[
// 				{
// 					"class":["cardDescription","static"], (requestDependency)
//                     ...
// 				}
// 			]
// 		},
// 		{
// 			class: ["flow.car"],
// 			entities:[
// 				{
// 					"class":["car"]
//                     ...
// 				}
// 			]
// 		}
// 	]
// }
function constructRequestEntity(
    requestDependencies,
    state,
    currentPath,
    sirenAction,
    currentWidgetId,
    requestWrapper
) {
    let requestEntity;
    let extractedEntities = requestDependencies.map(dependency => {
        let dependentStorePaths = getDependentPaths([dependency], state, currentPath);
        return {
            class: [dependency],
            entities: dependentStorePaths.map(dependentStorePath => {
                let result = state.getIn(["data", ...dependentStorePath]);
                return result ? result.toJS() : null;
            })
        };
    });

    //in case there is a single request dependency and requestWrapper is not set to true, return a single entity
    if (requestDependencies.length === 1 && !requestWrapper) {
        //returning the first extracted entity
        requestEntity = extractedEntities[0].entities[0];
    } else {
        requestEntity = {
            class: ["request"],
            entities: extractedEntities
        };
    }
    let outputParser = webuiState.getActionByName("widget.getOutputParser")(currentWidgetId, state);

    return createFormData(requestEntity, sirenAction, outputParser, currentWidgetId);
}

function saveResponse(response, targets, state, currentPath) {
    //allows multiple target entities to be updated with different data
    if (isCollectiveResponse(response)) {
        return saveCollectiveResponse(response, targets, state, currentPath);
    }
    return updateEntity(response, targets, state, currentPath);
}

//check for special response entity in the following format
//Example:
// {
// 	class: ["response"], (special response entity)
// 	entities: [
// 		{
// 			class: ["flow.cardDescription", "flow2.cardDescription"], (multiple targets possible)
// 			entities:[
// 				{
// 					"class":["cardDescription","static"] ...
// 				}]
// 		},
// 		{
// 			class: ["flow.car"], (targetDependency)
// 			entities:[
// 				{
// 					"class":["car"] ...
// 				}]
// 		}
// 	]
// }
//
// if BE returns entity with class "response" and actions in it,
// webui is going to dispatch the actions and ignore the entities
// {
// 	class: ["response"], (special response entity)
// 	actions: [
// 		{
// 			actionType: "switchDashboard"
// 			href: ...,
//          .....
// 		},
//      {
//      ...
//      }
//  entities: [{...},{...}]  <--- WILL BE IGNORED
//  ]
function isCollectiveResponse(response = {}) {
    return response["class"] ? response["class"][0] === "response" : false;
}

function isActionResponse(response) {
    return response.hasOwnProperty("actions") && response.actions.length;
}

//allows multiple target entities to be updated with different data
function saveCollectiveResponse(response, targets, state, currentPath) {
    return dispatch => {
        response.entities.forEach(entity =>
            //each entity class names represent targets to be updated
            dispatch(updateEntity(entity.entities[0], entity.class, state, currentPath))
        );
    };
}

function updateEntity(newData, targets, state, currentPath) {
    return dispatch => {
        let targetEntities = getDependentPaths(targets, state, currentPath);
        if (!targetEntities.length) {
            WEBUILogs.warn("updateAction: Missing response targets:");
            WEBUILogs.warn(targets);
            return Promise.reject("updateAction: Missing response targets.");
        }
        return dispatch(
            webuiState.getActionByName("component.triggerMultipleSet")(targetEntities, newData)
        );
    };
}

function isFlowWidget(widgetId, state) {
    let widgetActions = state.getIn(["data", widgetId, "actions"]) || [];
    let getAvailableStepsAction = widgetActions.find(
        action => action.get("actionType") === "getAvailableSteps"
    );
    return !!getAvailableStepsAction;
}
